import { type FormEvent, useEffect, useState } from "react"
import { Network, X } from "lucide-react"
import { smartoltService, type SmartOLTOltMgmtIpRow } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"

type MgmtMode = "static" | "dhcp" | "inactive"

type PoolDefaults = {
  subnet_mask?: string | null
  default_gateway?: string | null
  dns1?: string | null
  dns2?: string | null
}

type Props = {
  open: boolean
  onu: SmartOLTOltMgmtIpRow | null
  poolDefaults?: PoolDefaults
  onClose: () => void
  onSuccess: () => void
}

const emptyStatic = {
  vlan: "",
  ipv4_address: "",
  subnet_mask: "",
  gateway: "",
  dns1: "",
  dns2: "",
}

const emptyDhcp = {
  vlan: "",
  cvlan: "",
  svlan: "",
  tag_transform_mode: "",
}

export function SmartOLTMgmtIpModal({ open, onu, poolDefaults, onClose, onSuccess }: Props) {
  const [mode, setMode] = useState<MgmtMode>("static")
  const [saving, setSaving] = useState(false)
  const [staticForm, setStaticForm] = useState(emptyStatic)
  const [dhcpForm, setDhcpForm] = useState(emptyDhcp)

  useEffect(() => {
    if (!open || !onu) return
    setMode("static")
    setStaticForm({
      vlan: String(onu.mgmt_vlan || ""),
      ipv4_address: String(onu.ip_address || ""),
      subnet_mask: String(onu.subnet_mask || poolDefaults?.subnet_mask || ""),
      gateway: String(onu.default_gateway || poolDefaults?.default_gateway || ""),
      dns1: String(onu.dns1 || poolDefaults?.dns1 || ""),
      dns2: String(onu.dns2 || poolDefaults?.dns2 || ""),
    })
    setDhcpForm({ vlan: String(onu.mgmt_vlan || ""), cvlan: "", svlan: "", tag_transform_mode: "" })
  }, [open, onu, poolDefaults])

  if (!open || !onu) return null

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    try {
      if (mode === "inactive") {
        await smartoltService.setOnuMgmtIpInactive(onu.onu_external_id)
      } else if (mode === "dhcp") {
        const form_data = Object.fromEntries(
          Object.entries(dhcpForm).map(([key, value]) => [key, value.trim()]).filter(([, value]) => value),
        )
        if (!form_data.vlan) {
          toast.error("VLAN is required for DHCP mode")
          setSaving(false)
          return
        }
        await smartoltService.setOnuMgmtIpDhcp(onu.onu_external_id, form_data)
      } else {
        const form_data = Object.fromEntries(
          Object.entries(staticForm).map(([key, value]) => [key, value.trim()]).filter(([, value]) => value),
        )
        if (!form_data.vlan) {
          toast.error("VLAN is required for static mode")
          setSaving(false)
          return
        }
        await smartoltService.setOnuMgmtIpStatic(onu.onu_external_id, form_data)
      }
      await smartoltService.runCommandsNow(5)
      toast.success("Management IP command sent")
      onSuccess()
      onClose()
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to update management IP")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
      <form onSubmit={submit} className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-2xl animate-modal">
        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-200">
              <Network className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900">Configure Management IP</h2>
              <p className="text-xs text-slate-500">{onu.name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 flex gap-1.5 rounded-lg border border-slate-200 bg-slate-50/80 p-1">
          {(["static", "dhcp", "inactive"] as MgmtMode[]).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setMode(item)}
              className={`flex-1 h-8 rounded-md text-xs font-semibold capitalize transition-all shadow-xs ${
                mode === item
                  ? "bg-blue-600 text-white"
                  : "text-slate-600 hover:text-slate-900 hover:bg-white"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {mode === "static" && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              ["vlan", "VLAN", true],
              ["ipv4_address", "IPv4 address", false],
              ["subnet_mask", "Subnet mask", false],
              ["gateway", "Gateway", false],
              ["dns1", "DNS 1", false],
              ["dns2", "DNS 2", false],
            ].map(([field, label, required]) => (
              <label key={field} className="text-xs">
                <span className="text-slate-600 font-medium">
                  {label}
                  {required ? " *" : ""}
                </span>
                <input
                  value={staticForm[field as keyof typeof staticForm]}
                  onChange={(event) => setStaticForm((current) => ({ ...current, [field]: event.target.value }))}
                  className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                />
              </label>
            ))}
          </div>
        )}

        {mode === "dhcp" && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              ["vlan", "VLAN *"],
              ["cvlan", "C-VLAN"],
              ["svlan", "S-VLAN"],
              ["tag_transform_mode", "Tag transform"],
            ].map(([field, label]) => (
              <label key={field} className="text-xs">
                <span className="text-slate-600 font-medium">{label}</span>
                <input
                  value={dhcpForm[field as keyof typeof dhcpForm]}
                  onChange={(event) => setDhcpForm((current) => ({ ...current, [field]: event.target.value }))}
                  className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                />
              </label>
            ))}
          </div>
        )}

        {mode === "inactive" && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            Sets management IP mode to inactive for this ONU. Remote web/telnet management via this IP will be disabled.
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2.5 border-t border-slate-200 pt-3.5">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="h-9 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-50 transition-all"
          >
            {saving ? "Sending…" : "Apply"}
          </button>
        </div>
      </form>
    </div>
  )
}
